import React from "react";
import {
  Box,
  Heading,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
} from "recharts";

function StatCard({ label, value, suffix }) {
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.600");
  return (
    <Stat
      bg={cardBg}
      borderWidth="1px"
      borderColor={border}
      borderRadius="xl"
      px={4}
      py={3}
      boxShadow="sm"
    >
      <StatLabel fontSize="xs">{label}</StatLabel>
      <StatNumber fontSize="2xl">
        {value}
        {suffix ? (
          <Text as="span" fontSize="md" ml={1}>
            {suffix}
          </Text>
        ) : null}
      </StatNumber>
    </Stat>
  );
}

function ScoreChart({ title, data }) {
  const cardBg = useColorModeValue("white", "gray.800");
  const border = useColorModeValue("gray.200", "gray.600");
  const grid = useColorModeValue("#E2E8F0", "#2D3748");
  const axis = useColorModeValue("#718096", "#A0AEC0");

  const hasData = (data || []).some((d) => d.scorePct != null);

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={border}
      borderRadius="xl"
      p={4}
      boxShadow="sm"
    >
      <Heading size="xs" mb={3}>
        {title}
      </Heading>
      {hasData ? (
        <Box h="180px">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="caScore" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#805AD5" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#805AD5" stopOpacity={0.05} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={grid} />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: axis }} interval="preserveStartEnd" />
              <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: axis }} />
              <RechartsTooltip
                formatter={(v) => [`${v}%`, "Score"]}
                labelFormatter={(l) => `Date: ${l}`}
              />
              <Area
                type="monotone"
                dataKey="scorePct"
                stroke="#805AD5"
                strokeWidth={2}
                fill="url(#caScore)"
                connectNulls
                dot={{ r: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      ) : (
        <Text fontSize="sm" color="gray.500" py={8} textAlign="center">
          No attempts yet in this window. Take a quiz to see your trend.
        </Text>
      )}
    </Box>
  );
}

/**
 * Score analytics: overall stat cards + last-7-days and last-30-days graphs.
 * @param {{ analytics: { last7, last30, overall } | null }} props
 */
export default function CaAnalytics({ analytics }) {
  if (!analytics) return null;
  const { overall, last7, last30 } = analytics;

  return (
    <Box>
      <Heading size="sm" mb={3}>
        Your progress
      </Heading>
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
        <StatCard label="Quizzes taken" value={overall?.quizzes ?? 0} />
        <StatCard label="Questions" value={overall?.questions ?? 0} />
        <StatCard label="Average" value={overall?.avgPct ?? 0} suffix="%" />
        <StatCard label="Best" value={overall?.bestPct ?? 0} suffix="%" />
      </SimpleGrid>
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={3}>
        <ScoreChart title="Last 7 days" data={last7 || []} />
        <ScoreChart title="Last 30 days" data={last30 || []} />
      </SimpleGrid>
    </Box>
  );
}
